import { useEffect, useRef, useState } from 'react';
import MediaThumbnail from '../common/MediaThumbnail';
import { getCompositor, type NoiseTile } from './screenNoise/compositor';
import {
  SCREEN_NOISE_GRAIN_PX,
  SCREEN_NOISE_STRENGTH,
  bufferSize,
  fitContain,
  type Size,
} from './screenNoise/screenNoiseMath';

interface ProtectedImageProps {
  /** Resolved preview URL; '' / null / undefined = none (MediaThumbnail's fallbacks). */
  src: string | null | undefined;
  alt: string;
  state?: 'pending' | 'ready' | 'failed';
  /** On the canvas and on every fallback, so they all fill the same slot. */
  className?: string;
  /** Noise strength, 0–1. */
  strength?: number;
  /** Grain cell edge in CSS px. */
  grain?: number;
  /** The loaded picture's natural size, whenever a new picture has loaded. */
  onLoad?: (size: Size) => void;
}

// Start downloading a picture about one screen ahead of view (lazy, like
// <img loading="lazy">); animate it from slightly before it enters.
const LOAD_MARGIN = '100% 0px';
const ANIMATE_MARGIN = '25% 0px';

interface Loaded {
  src: string;
  img: HTMLImageElement;
}

/**
 * A photo drawn through moving grain (plan 2026-10-06-screen-noise): on
 * screen the eye averages the grain away, a screenshot keeps one grainy
 * frame. A layer on top of the server watermark, against phone screenshots
 * only — the network response is the watermarked preview as before.
 *
 * The canvas is a tile of the page-wide screen-noise compositor (one WebGL
 * context for every protected picture, see compositor.ts), which animates it
 * while it is in view. Never shows the bare image while protection is still
 * possible: the processing placeholder until the first image has loaded, then
 * the canvas, which keeps the current picture while the next src loads and
 * swaps it in place. The download starts only near the viewport, like a lazy
 * <img>. Falls back to the plain <img> (MediaThumbnail) where
 * WebGL is unavailable, the context is lost or the upload throws — for good,
 * so later photos skip the attempt.
 */
const ProtectedImage = ({
  src,
  alt,
  state,
  className,
  strength = SCREEN_NOISE_STRENGTH,
  grain = SCREEN_NOISE_GRAIN_PX,
  onLoad,
}: ProtectedImageProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const placeholderRef = useRef<HTMLElement>(null);
  const tileRef = useRef<NoiseTile | null>(null);
  // Read by the compositor on every frame, so new props apply without a new tile.
  const params = useRef({ strength, grain });
  params.current = { strength, grain };
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;
  const ratioRef = useRef(1);
  // Re-fits the tile's buffer to the current picture; set by the tile effect.
  const refitRef = useRef<() => void>(() => {});

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [plain, setPlain] = useState(false);
  // Near the viewport once: from then on every src loads at once (prev/next).
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined');

  const hasSource = Boolean(src) && state !== 'pending';

  // Wait for the placeholder to near the viewport before downloading.
  useEffect(() => {
    const placeholder = placeholderRef.current;
    if (near || !placeholder) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNear(true);
      },
      { rootMargin: LOAD_MARGIN },
    );
    observer.observe(placeholder);
    return () => observer.disconnect();
  }, [near, hasSource]);

  // Load with CORS so WebGL may read the pixels (the buckets allow GET from any origin).
  useEffect(() => {
    if (!src || state === 'pending' || plain || !near) return undefined;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      setLoaded({ src, img });
      onLoadRef.current?.({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      if (!cancelled) setFailedSrc(src);
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, state, plain, near]);

  const showCanvas = hasSource && !plain && failedSrc !== src && loaded !== null;

  // One compositor tile per canvas mount: sizing and visibility.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!showCanvas || !canvas) return undefined;
    const compositor = getCompositor();
    if (!compositor) {
      setPlain(true);
      return undefined;
    }
    const tile = compositor.addTile(canvas, () => params.current, () => setPlain(true));
    tileRef.current = tile;

    const refit = () => {
      const box = { width: canvas.clientWidth, height: canvas.clientHeight };
      const dpr = window.devicePixelRatio || 1;
      tile.setSize(bufferSize(fitContain(box, ratioRef.current), dpr, tile.maxSize));
    };
    refitRef.current = refit;

    const resizeObserver = new ResizeObserver(refit);
    resizeObserver.observe(canvas);
    // Visible from the start (it was just near the viewport), so the first
    // frame is drawn at once; the observer then reports the real state.
    tile.setVisible(true);
    const viewObserver =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver((entries) => tile.setVisible(entries.some((entry) => entry.isIntersecting)), {
            rootMargin: ANIMATE_MARGIN,
          });
    viewObserver?.observe(canvas);

    return () => {
      resizeObserver.disconnect();
      viewObserver?.disconnect();
      refitRef.current = () => {};
      tileRef.current = null;
      tile.dispose();
    };
  }, [showCanvas]);

  // Hand the loaded picture to the tile (first load and every later src).
  useEffect(() => {
    const tile = tileRef.current;
    if (!showCanvas || !tile || !loaded) return;
    ratioRef.current = loaded.img.naturalWidth / loaded.img.naturalHeight;
    refitRef.current();
    try {
      tile.setImage(loaded.img);
    } catch {
      // e.g. a SecurityError on a response the browser treats as tainted.
      setPlain(true);
    }
  }, [showCanvas, loaded]);

  if (!hasSource) {
    return <MediaThumbnail src="" state={state} alt={alt} className={className} />;
  }
  if (plain || failedSrc === src) {
    return <MediaThumbnail src={src} state={state} alt={alt} className={className} />;
  }
  if (!loaded) {
    return <MediaThumbnail ref={placeholderRef} src="" state="pending" alt={alt} className={className} />;
  }
  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={alt}
      className={className}
      style={{ aspectRatio: `${loaded.img.naturalWidth} / ${loaded.img.naturalHeight}` }}
    />
  );
};

export default ProtectedImage;
